import { Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EduContentBookApi, EduContentTOCApi } from '../+api';
import {
  EduContentBookInterface,
  EduContentTOCEduContentInterface,
  EduContentTOCEduContentMetadataInterface,
  EduContentTOCInterface,
  EvaluationWithSubjectsInterface,
} from '../+models';
import { WordFormInterface, WordInterface } from '../word';
import { EduContentTocServiceInterface } from './edu-content-toc.service.interface';

@Injectable({
  providedIn: 'root',
})
export class EduContentTocService implements EduContentTocServiceInterface {
  constructor(
    private eduContentTOCApi: EduContentTOCApi,
    private eduContentBookApi: EduContentBookApi
  ) {}

  getBooksByMethodIds(methodIds: number[]): Observable<EduContentBookInterface[]> {
    return this.eduContentBookApi.find({
      where: { methodId: { inq: methodIds } },
      include: [{ relation: 'years' }],
    });
  }

  getTree(bookId: number): Observable<EduContentTOCInterface[]> {
    return this.eduContentTOCApi.tree(bookId);
  }

  getBooksByIds(bookIds: number[]): Observable<EduContentBookInterface[]> {
    return this.eduContentBookApi.find({
      where: { id: { inq: bookIds } },
      include: [{ relation: 'years' }],
    });
  }

  getEduContentTocEduContentForBookId(bookId: number): Observable<EduContentTOCEduContentInterface[]> {
    return this.eduContentBookApi.getUnlockedExercisesRemote(bookId);
  }

  getEduContentTocsForBook(bookId: number): Observable<EduContentTOCInterface[]> {
    return this.eduContentTOCApi.getEduContentTocsForBook(bookId);
  }

  addEvaluation(eduContentTOCId: number): Observable<EvaluationWithSubjectsInterface> {
    return this.eduContentTOCApi.patchAttributes(eduContentTOCId, {
      isEvaluation: true,
    });
  }

  deleteEvaluation(eduContentTOCId: number): Observable<boolean> {
    return this.eduContentTOCApi.deleteEvaluation(eduContentTOCId);
  }

  moveEvaluation(oldEduContentTOCId: number, newEduContentTOCId: number): Observable<EvaluationWithSubjectsInterface> {
    return this.eduContentTOCApi.moveEvaluation(oldEduContentTOCId, newEduContentTOCId);
  }

  updateEduContentToc(
    eduContentTOCId: number,
    eduContentTOC: Partial<EduContentTOCInterface>
  ): Observable<EduContentTOCInterface> {
    return this.eduContentTOCApi.updateEduContentTOC(eduContentTOCId, eduContentTOC);
  }

  updateEduContentMetadataDisplayOrder(
    eduContentTOCId: number,
    displayOrderByEduContentMetadataId: Dictionary<number>
  ): Observable<EduContentTOCEduContentMetadataInterface[]> {
    return this.eduContentTOCApi.updateEduContentMetadataDisplayOrder(
      eduContentTOCId,
      displayOrderByEduContentMetadataId
    );
  }

  getWordsForToc(eduContentTOCId: number): Observable<WordInterface[]> {
    const asWordRoot = (word: WordInterface): string => {
      try {
        const forms = JSON.parse(word.forms ?? '[]') as WordFormInterface[];
        const baseForm = forms.find((form) => form.isBaseForm);
        if (baseForm?.label) {
          return baseForm.label;
        }
      } catch (error) {
        console.warn('Failed to parse word.forms:', error, word.forms);
      }

      return word.label;
    };

    const wordsForToc = this.eduContentTOCApi
      .getWordsByTocId(eduContentTOCId)
      .pipe(map((words) => words.sort((a, b) => asWordRoot(a).localeCompare(asWordRoot(b)))));

    return wordsForToc;
  }
}
